import { useState, useMemo, useEffect, useCallback } from 'react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart, PieChart, Pie, Cell } from 'recharts';
import { useDesktopTopBar } from '../../context/DesktopLayoutContext';
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { useDrillHistoryBack } from '../../hooks/useDrillHistoryBack';
import DesktopLayout from '../layout/DesktopLayout';
import TopBar, { DATE_PRESETS_DESKTOP, DATE_PRESETS_MOBILE } from '../layout/TopBar';
import Phone from '../Phone';
import BottomNav from '../BottomNav';
import KPI from '../KPI';
import KpiTooltip from '../KpiTooltip';
import { ReportSectionExportButton } from './components/ReportExportButton';
import { type ExportFormat } from '../../utils/reportExport';
import { getTopOffersApi } from '../../redux/reports/offersReport/offersReportApi';
import { T, useIsDesktop, Bone } from './reportTokens';
import './reports.css';
import {
    ApiTypeBadge, ApiStatusPill, fmtNum, fmtMoney, truncate,
    apiTypeColor, fmtPct, dateTickInterval, makeDateTickFormatter,
    DATE_AXIS_PADDING, dateAxisRightMargin, dateAxisTickProps, dateAxisExtraHeight,
} from './drills/offersData';
import OfferRedemptionTrendDrill from './drills/OfferRedemptionTrendDrill';
import OfferDiscountBreakdownDrill from './drills/OfferDiscountBreakdownDrill';
import OfferIndividualDrill from './drills/OfferIndividualDrill';
import { localizeCurrencyText, moneyHeader, useCurrencyRevision } from '../../utils/currency';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useSharedReportDateFilter } from '../../redux/reportFilters/reportFiltersHooks';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { fetchOfferRedemptionTrendRequest, fetchOfferDiscountBreakdownRequest, fetchOfferDiscountByTypeRequest, fetchTopOffersRequest, fetchOffersKpiRequest, fetchOfferDetailRequest, fetchOfferKpiComparisonRequest } from '../../redux/reports/offersReport/offersReportActions';
import { todayStr, yesterdayStr, getStartEndFromFilter, getFilterLabel } from '../../services/reportsApi';
import SectionError from './SectionError';
import SectionEmpty from './SectionEmpty';
import ErrorBoundary from '../ErrorBoundary';

// Location id can live under a few keys depending on the auth/branch shape; probe defensively (matches SalesScreen).
function findLocationId(obj: any, depth = 0): string | null {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId', 'outlet_id']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
}

type DrillNode = { kind: 'trend' } | { kind: 'discount' } | { kind: 'offer'; id: string; name?: string };

// Drills are a STACK, not a single value — the Discount Breakdown drill can open
// an offer drill on top of itself (Per-Offer Breakdown → offer), and backing out
// of that offer must land back on Discount Breakdown, not on the report root.
// Max depth is 2 (root → discount → offer); see the useDrillHistoryBack pair below.

// Prior-period delta — mirrors SnapshotScreen's getDeltaNode: date-aware label,
// 2-decimal %, arrow + sign, and a muted "vs <period>" suffix. `inverted` flips
// colour/arrow for "lower is better" metrics; the shown sign always matches the
// actual value.
function DeltaText({ pct, inverted = false, compact = false, label = 'vs prior' }: { pct: number | null; inverted?: boolean; compact?: boolean; label?: string }) {
    const size = compact ? 9 : 12;
    const labelSize = compact ? 8 : 11;
    if (pct == null || Number.isNaN(Number(pct))) return <span style={{ fontSize: labelSize, color: T.t3 }}>N/A {label}</span>;
    const num = Number(pct);
    const isZero = num === 0;
    const isGood = inverted ? num < 0 : num >= 0;
    const color = isZero ? T.t2 : isGood ? T.green : T.red;
    return (
        <span style={{ fontSize: size, color, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {!isZero && <>{isGood ? '↑' : '↓'} {num >= 0 ? '+' : '-'}</>}{Math.abs(num).toFixed(2)}%
            <span style={{ fontSize: labelSize, color: T.t3, fontWeight: 400, marginLeft: 4 }}>{label}</span>
        </span>
    );
}

// AOV (with Offers) KPI helper text — shown in the info (ⓘ) tooltip on desktop
// hover and mobile tap (ZAR-1471 ②).
const AOV_HELPER_TEXT = 'Average order value for orders where at least one offer was applied. Calculated as total revenue from offer-applied orders ÷ number of those orders.';

// Offers-table status filter (ZAR-1471 ③) — only these three statuses exist, so
// they're hard-coded on the FE. `value` is the enum sent in the `status` query
// param; the label is what the dropdown shows. No selection = All.
const OFFER_STATUS_OPTIONS: { label: string; value: string }[] = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Paused', value: 'PAUSED' },
    { label: 'Archived', value: 'ARCHIVED' },
];

// Mobile "Sort by" dropdown (ZAR-1471 ③) — the card list has no column headers to
// click, so sorting is a dropdown. Each option maps to the same server sortBy +
// sortOrder the desktop header sort uses. Default = Redemptions (high→low).
const OFFER_SORT_OPTIONS: { label: string; sortBy: 'redemptions' | 'discountGiven'; sortOrder: 'asc' | 'desc' }[] = [
    { label: 'Redemptions (high→low)', sortBy: 'redemptions', sortOrder: 'desc' },
    { label: 'Redemptions (low→high)', sortBy: 'redemptions', sortOrder: 'asc' },
    { label: 'Discount Given (high→low)', sortBy: 'discountGiven', sortOrder: 'desc' },
    { label: 'Discount Given (low→high)', sortBy: 'discountGiven', sortOrder: 'asc' },
];

function DesktopKPITile({ label, info, value, secondary, delta, deltaInverted, deltaLabel, drillable, onClick }: {
    label: string;
    info?: string;
    value: React.ReactNode;
    secondary?: React.ReactNode;
    delta?: number | null;
    deltaInverted?: boolean;
    deltaLabel?: string;
    drillable?: boolean;
    onClick?: () => void;
}) {
    return (
        <div
            onClick={drillable ? onClick : undefined}
            className={`rpt-tile rpt-card${drillable ? ' rpt-tile--drillable' : ''}`}
            style={{
                flex: 1,
                background: T.card,
                border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`,
                boxShadow: T.shadow,
            }}
        >
            <div className="rpt-tile__label" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>{label}{info && <KpiTooltip text={info} />}</div>
            <div className="rpt-tile__value" style={{ color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
            {secondary && <div style={{ fontSize: 11, color: T.t2, marginTop: 4 }}>{secondary}</div>}
            {delta !== undefined && <div style={{ marginTop: 6 }}><DeltaText pct={delta ?? null} inverted={!!deltaInverted} label={deltaLabel} /></div>}
            {drillable && <span style={{ position: 'absolute', top: 14, right: 14, color: T.accent, fontSize: 14 }}>›</span>}
        </div>
    );
}

function fmtDeltaString(pct: number | null): string | null {
    if (pct == null) return null;
    return `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`;
}

// Rows-per-page choices for the Offers table (ZAR-1471 ③): 10 (default), 20, or
// All. "All" loads every matching offer on a single page, but is capped server-
// side at ALL_ROWS_CAP to protect the query — the on-screen table then shows the
// first 100 with a footer note (export still emits the full set; see ④).
type PageSize = number | 'All';
const PAGE_SIZE_CHOICES: PageSize[] = [10, 20, 'All'];
const SMALLEST_PAGE_SIZE = 10;
const ALL_ROWS_CAP = 100;

// Pagination button style — mirrors SnapshotScreen's Items Overview footer so the
// Top Offers table paginates with the same look on desktop and mobile.
function pagBtn(disabled: boolean, isDesktop: boolean): React.CSSProperties {
    return {
        padding: isDesktop ? '4px 10px' : '3px 8px',
        borderRadius: isDesktop ? 6 : 5,
        border: `1px solid ${T.cardBorder}`,
        background: disabled ? T.hover : T.card,
        color: disabled ? T.t3 : T.t1,
        fontSize: isDesktop ? 11 : 10,
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
    };
}

const OFFER_NAME_TRUNC_DESKTOP = 30;
const OFFER_NAME_TRUNC_MOBILE = 26;

// Desktop offer-name cell — the name still drills into the offer on click, but a
// truncated name reveals the full text in a custom hover tooltip (deliberately NOT
// the native `title` attribute, so it matches the report's styling/theme).
function OfferNameDesktopCell({ name: apiName, onOpen }: { name: string; onOpen: () => void }) {
    const [hover, setHover] = useState(false);
    // Names are backend text and can carry a currency symbol ("$5 Off First Order").
    const name = localizeCurrencyText(apiName);
    const isTruncated = name.length > OFFER_NAME_TRUNC_DESKTOP;
    return (
        <span
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <button
                type="button"
                onClick={onOpen}
                style={{ color: T.accent, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, textAlign: 'left' }}
            >
                {truncate(name, OFFER_NAME_TRUNC_DESKTOP)}
            </button>
            {isTruncated && hover && (
                <span
                    role="tooltip"
                    style={{
                        position: 'absolute',
                        left: 0,
                        bottom: 'calc(100% + 6px)',
                        zIndex: 600,
                        background: T.t1,
                        color: T.card,
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        padding: '6px 10px',
                        borderRadius: 6,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                        whiteSpace: 'normal',
                        width: 'max-content',
                        maxWidth: 320,
                        pointerEvents: 'none',
                    }}
                >
                    {name}
                </span>
            )}
        </span>
    );
}

// Mobile offer-name — tapping a truncated name expands it onto a second line (and
// taps again to collapse) instead of drilling; the tap is stopped from bubbling to
// the card's drill handler. Names that already fit fall through to the card drill.
function OfferNameMobileCell({ name: apiName }: { name: string }) {
    const [expanded, setExpanded] = useState(false);
    const name = localizeCurrencyText(apiName);
    const isTruncated = name.length > OFFER_NAME_TRUNC_MOBILE;
    return (
        <div
            onClick={isTruncated ? (e) => { e.stopPropagation(); setExpanded((v) => !v); } : undefined}
            style={{
                flex: 1,
                minWidth: 0,
                marginRight: 8,
                fontSize: 13,
                color: T.accent,
                fontWeight: 600,
                cursor: isTruncated ? 'pointer' : 'inherit',
                whiteSpace: expanded ? 'normal' : 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
            }}
        >
            {expanded ? name : truncate(name, OFFER_NAME_TRUNC_MOBILE)}
        </div>
    );
}

function OffersReportContent({ isDesktop }: { isDesktop: boolean }) {
    const setTopBarProps = useDesktopTopBar();
    // Re-render when the outlet's country resolves (or changes), so backend text
    // carrying a currency symbol — offer names, offer types — is re-localised.
    const currencyRevision = useCurrencyRevision();
    // Drill stack — the top of the stack is the visible drill; [] is the report root.
    const [drillStack, setDrillStack] = useState<DrillNode[]>([]);
    const drill = drillStack.length ? drillStack[drillStack.length - 1] : null;
    const openDrill = useCallback((d: DrillNode) => setDrillStack((s) => [...s, d]), []);
    const [datePeriod, setDatePeriod] = useSharedReportDateFilter('Today');
    const [sortBy, setSortBy] = useState<'redemptions' | 'discountGiven'>('redemptions');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);
    // Offers table page size — 10 by default (③), overridable to 20 or "All" via
    // the row-limit selector. `effectiveSize` is the numeric `size` sent to the
    // server: "All" resolves to the ALL_ROWS_CAP (100) so the query stays bounded.
    const [pageSize, setPageSize] = useState<PageSize>(10);
    const [pageSizeMenuOpen, setPageSizeMenuOpen] = useState(false);
    const effectiveSize = pageSize === 'All' ? ALL_ROWS_CAP : pageSize;
    // ── Offers-table filters (ZAR-1471 ③) — server-side via `search` + `status`
    //    query params. `searchInput` is the raw text; `searchQuery` is its 300ms-
    //    debounced value (what actually hits the API). `statusFilter` holds the
    //    selected status enums (empty = All). All reset to page 1 on change and do
    //    NOT persist across navigation (component-local state, gone on unmount).
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [sortMenuOpen, setSortMenuOpen] = useState(false);
    // Comma-joined enum string for the `status` param (stable key for effect deps).
    const statusParam = statusFilter.join(',');

    // ── Redemption Trend API (/sales/offers/redemption-trend) — powers the main
    //    Total Redemptions KPI + trend chart, and the drilldown's stamp KPIs,
    //    trend chart and order-type breakdown. Today omits startDate/endDate.
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const dispatch = useAppDispatch();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? '', [auth, branch]);
    const redemptionTrend = useAppSelector((s) => s.offersReport.redemptionTrend);
    const redemptionTrendLoading = useAppSelector((s) => s.offersReport.redemptionTrendLoading);
    const redemptionTrendFailure = useAppSelector((s) => s.offersReport.redemptionTrendFailure);

    // ── KPI Comparison API (/sales/offers/kpi-comparison) — supplies the
    //    "vs prior" deltas for Total Redemptions & Total Discount Given, plus the
    //    AOV (with Offers) KPI value and its own delta. Today omits dates.
    const kpiComparison = useAppSelector((s) => s.offersReport.kpiComparison);
    const kpiComparisonLoading = useAppSelector((s) => s.offersReport.kpiComparisonLoading);
    const kpiComparisonFailure = useAppSelector((s) => s.offersReport.kpiComparisonFailure);

    // ── Discount Breakdown API (/sales/offers/discount-breakdown) — powers the
    //    main Total Discount Given KPI and the discount drilldown's stamp KPIs,
    //    per-type progress bars and per-offer breakdown. Today omits dates.
    const discountBreakdown = useAppSelector((s) => s.offersReport.discountBreakdown);
    const discountBreakdownLoading = useAppSelector((s) => s.offersReport.discountBreakdownLoading);
    const discountBreakdownFailure = useAppSelector((s) => s.offersReport.discountBreakdownFailure);

    // ── Discount by Type API (/sales/offers/discount-by-type) — powers the
    //    "Discount by Type" donut (cost split by mechanic + centre total). Today
    //    omits startDate/endDate.
    const discountByType = useAppSelector((s) => s.offersReport.discountByType);
    const discountByTypeLoading = useAppSelector((s) => s.offersReport.discountByTypeLoading);
    const discountByTypeFailure = useAppSelector((s) => s.offersReport.discountByTypeFailure);

    // ── Top Offers API (/sales/offers/top-offers) — powers the Top Offers table:
    //    server-paginated + server-sorted (redemptions | discountGiven), with the
    //    totalElements count feeding the header "N offers" tally. Today omits dates.
    const topOffers = useAppSelector((s) => s.offersReport.topOffers);
    const topOffersLoading = useAppSelector((s) => s.offersReport.topOffersLoading);
    const topOffersFailure = useAppSelector((s) => s.offersReport.topOffersFailure);
    const topOffersTotalElements = useAppSelector((s) => s.offersReport.topOffersTotalElements);
    const topOffersTotalPages = useAppSelector((s) => s.offersReport.topOffersTotalPages);

    // ── Offers KPI row (Active Offers + Top Offer) — a SEPARATE unfiltered call to
    //    /sales/offers/top-offers (page 1, size 1, redemptions desc, no search or
    //    status). These two tiles describe the whole selected period, so they must
    //    not move when the table below is searched, status-filtered, sorted or
    //    paged — which is exactly what happened while they read the table's slice.
    //    Only locationId / date period re-issue this request.
    const activeOffers = useAppSelector((s) => s.offersReport.offersKpiActiveOffers);
    const kpiTopOffer = useAppSelector((s) => s.offersReport.offersKpiTopOffer);
    const offersKpiLoading = useAppSelector((s) => s.offersReport.offersKpiLoading);
    const offersKpiLoaded = useAppSelector((s) => s.offersReport.offersKpiLoaded);
    const offersKpiFailure = useAppSelector((s) => s.offersReport.offersKpiFailure);

    // ── Offer Detail API (/sales/offers/detail) — powers the individual offer
    //    drilldown (offer metadata, stamp KPIs, daily-redemption chart, top
    //    items, order-type breakdown, promo codes). offerId is passed from the
    //    opener (Top Offer KPI's own record, or a clicked table row).
    //    Today omits startDate/endDate.
    const offerDetail = useAppSelector((s) => s.offersReport.offerDetail);
    const offerDetailLoading = useAppSelector((s) => s.offersReport.offerDetailLoading);
    const offerDetailFailure = useAppSelector((s) => s.offersReport.offerDetailFailure);

    const isToday = datePeriod === 'Today';
    const dateRange = useMemo(
        () => getStartEndFromFilter(datePeriod, todayStr(), yesterdayStr()),
        [datePeriod],
    );

    const fetchTrend = useCallback(() => {
        if (!locationId) return;
        if (isToday) {
            dispatch(fetchOfferRedemptionTrendRequest(locationId));
        } else if (dateRange) {
            dispatch(fetchOfferRedemptionTrendRequest(locationId, dateRange.startDate, dateRange.endDate));
        }
    }, [dispatch, locationId, isToday, dateRange]);

    const fetchKpiComparison = useCallback(() => {
        if (!locationId) return;
        if (isToday) {
            dispatch(fetchOfferKpiComparisonRequest(locationId));
        } else if (dateRange) {
            dispatch(fetchOfferKpiComparisonRequest(locationId, dateRange.startDate, dateRange.endDate));
        }
    }, [dispatch, locationId, isToday, dateRange]);

    const fetchDiscount = useCallback(() => {
        if (!locationId) return;
        if (isToday) {
            dispatch(fetchOfferDiscountBreakdownRequest(locationId));
        } else if (dateRange) {
            dispatch(fetchOfferDiscountBreakdownRequest(locationId, dateRange.startDate, dateRange.endDate));
        }
    }, [dispatch, locationId, isToday, dateRange]);

    const fetchDiscountByType = useCallback(() => {
        if (!locationId) return;
        if (isToday) {
            dispatch(fetchOfferDiscountByTypeRequest(locationId));
        } else if (dateRange) {
            dispatch(fetchOfferDiscountByTypeRequest(locationId, dateRange.startDate, dateRange.endDate));
        }
    }, [dispatch, locationId, isToday, dateRange]);

    const fetchTopOffers = useCallback(() => {
        if (!locationId) return;
        const base = { locationId, page, size: effectiveSize, sortBy, sortOrder, search: searchQuery, status: statusParam } as const;
        if (isToday) {
            dispatch(fetchTopOffersRequest({ ...base }));
        } else if (dateRange) {
            dispatch(fetchTopOffersRequest({ ...base, startDate: dateRange.startDate, endDate: dateRange.endDate }));
        }
    }, [dispatch, locationId, isToday, dateRange, page, effectiveSize, sortBy, sortOrder, searchQuery, statusParam]);

    // Active Offers + Top Offer KPIs — deliberately depends ONLY on location and
    // date range. The table's search/status/sort/page state is NOT in the dep list,
    // so filtering the table never re-fires this request or changes the tiles.
    const fetchOffersKpi = useCallback(() => {
        if (!locationId) return;
        if (isToday) {
            dispatch(fetchOffersKpiRequest(locationId));
        } else if (dateRange) {
            dispatch(fetchOffersKpiRequest(locationId, dateRange.startDate, dateRange.endDate));
        }
    }, [dispatch, locationId, isToday, dateRange]);

    // Offer detail is fetched only while an offer drill is open — offerId comes
    // from the drill state (Top Offer KPI's own record, or a clicked table row).
    const activeOfferId = drill?.kind === 'offer' ? drill.id : null;
    const fetchDetail = useCallback(() => {
        if (!locationId || !activeOfferId) return;
        const base = { locationId, offerId: activeOfferId } as const;
        if (isToday) {
            dispatch(fetchOfferDetailRequest({ ...base }));
        } else if (dateRange) {
            dispatch(fetchOfferDetailRequest({ ...base, startDate: dateRange.startDate, endDate: dateRange.endDate }));
        }
    }, [dispatch, locationId, activeOfferId, isToday, dateRange]);

    useEffect(() => { fetchTrend(); }, [fetchTrend]);
    useEffect(() => { fetchKpiComparison(); }, [fetchKpiComparison]);
    useEffect(() => { fetchDiscount(); }, [fetchDiscount]);
    useEffect(() => { fetchDiscountByType(); }, [fetchDiscountByType]);
    useEffect(() => { fetchTopOffers(); }, [fetchTopOffers]);
    useEffect(() => { fetchOffersKpi(); }, [fetchOffersKpi]);
    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    // Debounce the search box 300ms — only the settled value hits the API.
    useEffect(() => {
        const t = setTimeout(() => setSearchQuery(searchInput), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Changing the date filter, search text or status filter re-scopes the
    // paginated table — jump back to page 1 so we never sit on a page beyond the
    // new result set's totalPages. (Sort already resets page 1 in handleSort.)
    useEffect(() => { setPage(1); }, [datePeriod, searchQuery, statusParam]);

    // ── Drill navigation — back button (top bar), swipe-back and hardware/
    //    browser back, matching the other report screens' drilldowns. Back pops
    //    ONE level, so offer-opened-from-discount returns to Discount Breakdown.
    const isDrill = drill !== null;
    const goBack = useCallback(() => setDrillStack((s) => s.slice(0, -1)), []);
    useSwipeBack();
    // useDrillHistoryBack keys its history marker on a boolean, so one call can only
    // track one level. Registering it once per depth gives each drill its own marker
    // and handler; the hook fires only the innermost, so hardware/browser back pops
    // a single level too. Call order matters — depth 1 must register before depth 2.
    useDrillHistoryBack(drillStack.length >= 1, goBack);
    useDrillHistoryBack(drillStack.length >= 2, goBack);

    // Changing the date range re-scopes the whole report, so the Offers table's
    // own controls must go back to their defaults with it (#24) — a status or
    // sort left over from the previous period silently narrows/reorders the new
    // one. Reset happens in the handler (not an effect) so the re-fetch fires
    // once with the new date AND the cleared filters, instead of once with the
    // stale filters and again after the reset. Page 1 is handled by the
    // datePeriod effect above; searchInput is cleared alongside searchQuery so
    // the debounce doesn't write the old text back.
    const handleDatePeriodChange = useCallback((p: string) => {
        if (p === datePeriod) return;
        setDatePeriod(p);
        setSearchInput('');
        setSearchQuery('');
        setStatusFilter([]);
        setStatusMenuOpen(false);
        setSortBy('redemptions');
        setSortOrder('desc');
        setSortMenuOpen(false);
    }, [datePeriod]);

    // ── All hooks must run on every render, before any early returns ────────
    // Discount by Type donut segments come from /sales/offers/discount-by-type.
    // Type and percentage are rendered exactly as the API sends them (FLAT,
    // 5.58) — no relabelling, no re-derived or re-rounded share; only the
    // currency symbol inside the label is localised to the outlet ("$ Off" →
    // "₹ Off" outside the US). The slice colour is looked up from the RAW type.
    const discountByTypeSegments = useMemo(
        () => (discountByType?.segments ?? []).map((s) => ({
            name: localizeCurrencyText(s.type),
            value: s.discountAmount,
            pct: fmtPct(s.percentage),
            color: apiTypeColor(s.type),
        })),
        // currencyRevision: re-map when the outlet's country resolves or changes.
        [discountByType, currencyRevision],
    );
    const discountByTypeTotal = discountByType?.totalDiscount ?? 0;

    // ── Top bar — matches the other report screens (Today + historical date
    //    presets). A drilldown moves the back button + drill title into the top
    //    bar (DrillFrame renders body-only), mirroring Product/EOD on desktop
    //    and mobile. Date pills are hidden while drilling.
    const drillTitle =
        drill?.kind === 'trend' ? 'Redemption Trend'
        : drill?.kind === 'discount' ? 'Discount Breakdown'
        : drill?.kind === 'offer' ? localizeCurrencyText(drill.name ?? offerDetail?.offerName ?? 'Offer')
        : null;
    useEffect(() => {
        if (isDrill) {
            // Drilldown — back button + drill title live in the top bar (like the
            // other report screens); desktop shows a leftAddon back button, mobile
            // uses the top bar's own back. Date pills are hidden while drilling.
            setTopBarProps({
                breadcrumbs: 'Reports › Offers Report',
                title: drillTitle ?? 'Offers',
                onBack: isDesktop ? undefined : goBack,
                leftAddon: isDesktop ? (
                    <div onClick={goBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginRight: 4 }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                    </div>
                ) : undefined,
                dateFilters: undefined,
            });
        } else {
            setTopBarProps({
                breadcrumbs: 'Reports',
                title: isDesktop ? 'Offers Report' : 'Offers',
                dateFilters: {
                    value: datePeriod,
                    onChange: handleDatePeriodChange,
                    options: isDesktop ? DATE_PRESETS_DESKTOP : DATE_PRESETS_MOBILE,
                },
            });
        }
    }, [setTopBarProps, datePeriod, handleDatePeriodChange, isDrill, isDesktop, drillTitle, goBack]);

    // ── Drill takes over the screen ─────────────────────────────────────────
    if (drill?.kind === 'trend') {
        return (
            <main style={{ color: T.t1 }}>
                <OfferRedemptionTrendDrill data={redemptionTrend} loading={redemptionTrendLoading} failure={redemptionTrendFailure} onRetry={fetchTrend} />
            </main>
        );
    }
    if (drill?.kind === 'discount') {
        return (
            <main style={{ color: T.t1 }}>
                <OfferDiscountBreakdownDrill data={discountBreakdown} loading={discountBreakdownLoading} failure={discountBreakdownFailure} onRetry={fetchDiscount} onOpenOffer={(id, name) => openDrill({ kind: 'offer', id, name })} />
            </main>
        );
    }
    if (drill?.kind === 'offer') {
        return (
            <main style={{ color: T.t1 }}>
                <OfferIndividualDrill data={offerDetail} loading={offerDetailLoading} failure={offerDetailFailure} onRetry={fetchDetail} />
            </main>
        );
    }

    // ── Normal report content ───────────────────────────────────────────────
    // Total Redemptions comes from /sales/offers/redemption-trend, the Top
    // Offers table from /sales/offers/top-offers, the Discount by Type donut
    // from /sales/offers/discount-by-type, and the AOV value + the top-row
    // "vs prior" deltas from /sales/offers/kpi-comparison. The Active Offers and
    // Top Offer KPIs read their OWN unfiltered /sales/offers/top-offers call
    // (offersKpi* in the slice), so they stay scoped to the selected period while
    // the table below is searched, status-filtered or sorted.
    const redemptionsValue = redemptionTrend?.totalRedemptions ?? 0;
    const dailyTrend = redemptionTrend?.dailyTrend ?? [];
    // Trend card height — the loading/error/empty placeholders track the chart so
    // the card doesn't resize between states. Below desktop the x-axis labels are
    // rotated (see `dateAxisTickProps`) and the extra height pays for the taller
    // tick band rather than eating into the plot area.
    const trendChartHeight = (isDesktop ? 220 : 140) + dateAxisExtraHeight(isDesktop);
    // Total Discount Given KPI reads /sales/offers/discount-breakdown.
    const discountKpiValue = discountBreakdown?.totalDiscount ?? 0;
    // AOV (with Offers) value + all three top-row deltas read /sales/offers/kpi-comparison.
    const aov = kpiComparison?.avgOrderValueWithOffers ?? 0;
    const redemptionsDelta = kpiComparison?.totalRedemptionsDelta ?? null;
    const discountDelta = kpiComparison?.totalDiscountGivenDelta ?? null;
    const aovDelta = kpiComparison?.avgOrderValueWithOffersDelta ?? null;
    // "This Month" and a custom range have no well-defined prior period to compare
    // against, so the KPI deltas are hidden entirely for those filters (desktop and
    // mobile alike). The custom filter arrives as a "custom:<from>|<to>" value.
    const showDelta = datePeriod !== 'This Month' && !datePeriod.startsWith('custom');
    // Date-aware "vs <period>" label — mirrors SnapshotScreen's priorLabel.
    const priorLabel = datePeriod === 'Today' ? 'vs Yesterday'
        : datePeriod === 'Yesterday' ? 'vs Previous day'
        : datePeriod === '7D' ? 'vs Last 7D'
        : datePeriod === '30D' ? 'vs Last 30D'
        : 'vs prior period';
    // Top Offer KPI reads the unfiltered offers-KPI call's single record — NOT the
    // table's first visible row, which follows the table's search/status/sort.
    // Clicking it drills into /sales/offers/detail for that offerId.
    const topOfferRow = kpiTopOffer;

    // Top Offers table is server-paginated + server-sorted via /sales/offers/top-offers;
    // rows and page count come straight from the API response. In "All" mode the
    // table is a single (capped) page, so pagination is suppressed (totalPages = 1).
    const isAllRows = pageSize === 'All';
    // "All" is capped at ALL_ROWS_CAP: when more matched, only the first 100 render.
    const isCapped = isAllRows && topOffersTotalElements > ALL_ROWS_CAP;
    const totalPages = isAllRows ? 1 : Math.max(1, topOffersTotalPages);
    const pageRows = topOffers;
    const showFrom = topOffersTotalElements === 0 ? 0 : (page - 1) * effectiveSize + 1;
    const showTo = Math.min(page * effectiveSize, topOffersTotalElements);
    const countText = `Showing ${showFrom}–${showTo} of ${topOffersTotalElements} offer${topOffersTotalElements === 1 ? '' : 's'}`;

    // ── Offers table export (ZAR-1471 ④) ────────────────────────────────────
    // CSV/PDF of the FULL filtered + sorted result set (fetch-all via
    // size = totalElements), not just the visible page — so it IGNORES the 100-row
    // "All" display cap. Same query params the table uses (locationId, date range,
    // sortBy/sortOrder, search, status). Named offers_{outlet}_{daterange}; when the
    // on-screen table is capped, a `_filtered` context hint is appended.
    // NOTE: the Excel (.xlsx) third format stays CSV+PDF for now (native CSV already
    // emits real .xlsx) — flagged to the user.
    const outletName = (branch as any)?.locationName ?? (branch as any)?.name ?? (branch as any)?.branchName ?? 'Outlet';
    const outletSlug = String(outletName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'outlet';
    const dateSlug = isToday
        ? todayStr()
        : dateRange
            ? (dateRange.startDate === dateRange.endDate ? dateRange.startDate : `${dateRange.startDate}_${dateRange.endDate}`)
            : todayStr();
    const exportFilename = `offers_${outletSlug}_${dateSlug}${isCapped ? '_filtered' : ''}`;
    // "Date range" line in the "All outlets" file header. Without this the button
    // falls back to the literal placeholder "Selected period", which told the
    // reader nothing about which period the sheet actually covers (OFR bug #22).
    const exportDateRangeLabel = getFilterLabel(datePeriod, dateRange?.startDate, dateRange?.endDate);
    // Discount Given must carry EXACTLY 2 decimals in every exported file (CSV /
    // XLSX / PDF) so the column matches the on-screen fmtMoney values and columns
    // sum cleanly in Excel. The shared formatMonetaryExport leaves whole numbers
    // bare (91 → "91", 700 → "700"), which made the exported column look like a
    // mix of money and counts — see OFR bug #20.
    const fmtDiscountExport = (v: number | string | null | undefined) => {
        const n = Number(v);
        return v === null || v === undefined || v === '' || Number.isNaN(n) ? '—' : n.toFixed(2);
    };
    const buildOffersExport = async (_format?: ExportFormat, opts?: { locationId?: string }) => {
        // "All outlets" export calls this once per accessible outlet with that
        // outlet's id — honour it, or every outlet's block repeats THIS outlet's
        // rows (see OFR bug #21).
        const loc = opts?.locationId ?? locationId;
        const dateParams = isToday || !dateRange ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const query = (size: number) => getTopOffersApi({ locationId: loc, page: 1, size, sortBy, sortOrder, search: searchQuery, status: statusParam, ...dateParams });
        // The fetch-all needs a row count up front, and topOffersTotalElements is
        // THIS outlet's — meaningless for an override outlet. Rather than spend a
        // size:1 probe per outlet (for an outlet with 0 or 1 offers that probe is
        // byte-identical to the fetch behind it — two identical calls per outlet in
        // the network log), the FIRST request is already the fetch-all, sized from
        // this outlet's count as the opening guess. The response carries the target
        // outlet's real totalElements, so a second call goes out only when that
        // outlet genuinely holds more rows than the guess covered.
        const guess = Math.max(1, topOffersTotalElements || 1);
        let res: any = await query(guess);
        const total = Number(res?.totalElements ?? res?.total ?? 0);
        if (total > guess) res = await query(total);
        // Fall back to the on-screen rows ONLY for this outlet; an override outlet
        // with a missing or malformed response must contribute nothing, not our rows.
        const list: any[] = Array.isArray(res?.content) ? res.content : (opts?.locationId ? [] : topOffers);
        return {
            // Money header carries the outlet's own symbol — "Discount Given (₹)" /
            // "Discount Given ($)" — or a bare "Discount Given" when the outlet's
            // country maps to no symbol (never "Discount Given ()").
            // Name/Type cells get the same currency rewrite the table shows.
            headers: ['Rank', 'Offer Name', 'Type', 'Redemptions', moneyHeader('Discount Given'), 'Status'],
            rows: list.map((o: any, i: number) => [i + 1, localizeCurrencyText(o.offerName), localizeCurrencyText(o.offerType), o.redemptions, fmtDiscountExport(o.discountGiven), o.status]),
            // Rank / Redemptions / Discount Given are quantities: keep them numeric
            // in the CSV so Excel right-aligns and sums them instead of force-texting
            // "700.00" (any ".00"/".50" value hits the trailing-zero text rule) into a
            // left-aligned string. The .xlsx path types numbers itself and ignores this.
            numericColumns: [0, 3, 4],
        };
    };
    const canExportOffers = !topOffersFailure && topOffersTotalElements > 0;

    // Whether any offers-table filter is active — drives the "no matches" empty
    // state (with a Clear link) vs the plain "no offers" empty state.
    const filtersActive = searchQuery.trim().length > 0 || statusFilter.length > 0;
    const clearOfferFilters = () => { setSearchInput(''); setSearchQuery(''); setStatusFilter([]); };
    const toggleStatus = (value: string) => {
        setStatusFilter((cur) => cur.includes(value) ? cur.filter((s) => s !== value) : [...cur, value]);
    };
    const statusLabel = statusFilter.length === 0
        ? 'All'
        : statusFilter.length === 1
            ? (OFFER_STATUS_OPTIONS.find((o) => o.value === statusFilter[0])?.label ?? '1 selected')
            : `${statusFilter.length} selected`;

    // Mobile sort dropdown — resolve the active option's label and apply a choice
    // (sets server sortBy + sortOrder and resets to page 1, like handleSort).
    const currentSortLabel = OFFER_SORT_OPTIONS.find((o) => o.sortBy === sortBy && o.sortOrder === sortOrder)?.label ?? OFFER_SORT_OPTIONS[0].label;
    const applySort = (o: typeof OFFER_SORT_OPTIONS[number]) => {
        setSortBy(o.sortBy);
        setSortOrder(o.sortOrder);
        setPage(1);
        setSortMenuOpen(false);
    };

    const handlePageSize = (size: PageSize) => { setPageSize(size); setPage(1); };

    // Rows-per-page selector — mirrors SnapshotScreen's Items Overview control. Shown
    // once there are more rows than the smallest choice (otherwise paging is moot).
    const pageSizeFontSize = isDesktop ? 11 : 10;
    const showSizeSelector = topOffersTotalElements > SMALLEST_PAGE_SIZE;
    const PageSizeSelector = showSizeSelector ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: pageSizeFontSize, color: T.t3, fontFamily: T.font }}>
            Rows per page:
            <div style={{ position: 'relative' }}>
                <button
                    type="button"
                    onClick={() => setPageSizeMenuOpen((o) => !o)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: pageSizeFontSize, fontWeight: 600, color: T.t1, fontFamily: T.font, padding: isDesktop ? '3px 6px' : '2px 4px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.card, cursor: 'pointer', lineHeight: 1 }}
                >
                    {pageSize}
                    <span className="material-symbols-rounded" style={{ fontSize: pageSizeFontSize + 2, lineHeight: 1, color: T.t3 }}>expand_more</span>
                </button>
                {pageSizeMenuOpen && (
                    <>
                        <div onClick={() => setPageSizeMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500 }} />
                        <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 501, overflow: 'hidden', minWidth: '100%' }}>
                            {PAGE_SIZE_CHOICES.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => { handlePageSize(s); setPageSizeMenuOpen(false); }}
                                    style={{ display: 'block', width: '100%', textAlign: 'center', fontSize: pageSizeFontSize, fontWeight: s === pageSize ? 700 : 500, color: s === pageSize ? T.accent : T.t1, fontFamily: T.font, padding: '7px 14px', border: 'none', background: s === pageSize ? `${T.accent}12` : 'transparent', cursor: 'pointer' }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    ) : null;

    const handleSort = (col: 'redemptions' | 'discountGiven') => {
        if (sortBy === col) setSortOrder(d => d === 'desc' ? 'asc' : 'desc');
        else { setSortBy(col); setSortOrder('desc'); }
        setPage(1);
    };

    // Mobile KPI deltas — ready-made percentages from /sales/offers/kpi-comparison
    // rendered as signed "+/-x.xx%" strings with an up/down flag.
    const redemptionsDeltaStr = fmtDeltaString(redemptionsDelta);
    const discountDeltaStr = fmtDeltaString(discountDelta);
    const aovDeltaStr = fmtDeltaString(aovDelta);

    return (
        <main className={isDesktop ? 'rpt-page' : ''} style={{ color: T.t1, padding: isDesktop ? 24 : 0, fontFamily: T.font, display: 'flex', flexDirection: 'column', gap: isDesktop ? 24 : 10 }}>
            {isDesktop ? (
                <>
                {/* KPI ROW — desktop uses rpt-tile rpt-card pattern, matches Sales/Product */}
                <div className="rpt-flex-row rpt-gap-16">
                    <DesktopKPITile label="Total Redemptions" value={redemptionTrendLoading && !redemptionTrend ? <Bone w={90} h={28} r={4} /> : redemptionTrendFailure ? '—' : fmtNum(redemptionsValue)} delta={showDelta && kpiComparison ? redemptionsDelta : undefined} deltaLabel={priorLabel} drillable onClick={() => openDrill({ kind: 'trend' })} />
                    <DesktopKPITile label="Total Discount Given" value={discountBreakdownLoading && !discountBreakdown ? <Bone w={90} h={28} r={4} /> : discountBreakdownFailure ? '—' : fmtMoney(discountKpiValue)} delta={showDelta && kpiComparison ? discountDelta : undefined} deltaLabel={priorLabel} drillable onClick={() => openDrill({ kind: 'discount' })} />
                    <DesktopKPITile label="AOV (with Offers)" info={AOV_HELPER_TEXT} value={kpiComparisonLoading && !kpiComparison ? <Bone w={90} h={28} r={4} /> : kpiComparisonFailure ? '—' : fmtMoney(aov)} delta={showDelta && kpiComparison ? aovDelta : undefined} deltaLabel={priorLabel} />
                    <DesktopKPITile label="Active Offers" value={offersKpiLoading && !offersKpiLoaded ? <Bone w={90} h={28} r={4} /> : offersKpiFailure ? '—' : fmtNum(activeOffers)} />
                    <DesktopKPITile
                        label="Top Offer"
                        value={offersKpiLoading && !offersKpiLoaded ? <Bone w={90} h={28} r={4} /> : offersKpiFailure ? '—' : topOfferRow ? truncate(localizeCurrencyText(topOfferRow.offerName), 22) : '—'}
                        secondary={topOfferRow ? `${fmtNum(topOfferRow.redemptions)} redemptions` : undefined}
                        drillable={!!topOfferRow}
                        onClick={topOfferRow ? () => openDrill({ kind: 'offer', id: topOfferRow.offerId, name: topOfferRow.offerName }) : undefined}
                    />
                </div>
                </>
            ) : (
                <>
                {/* Mobile KPI rows — shared <KPI> component, matches Sales/Product */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <KPI label="Redemptions" value={redemptionTrendLoading && !redemptionTrend ? <Bone w={50} h={18} r={4} style={{ margin: '4px auto 0' }} /> : redemptionTrendFailure ? '—' : fmtNum(redemptionsValue)} delta={showDelta && kpiComparison ? redemptionsDeltaStr : null} up={(redemptionsDelta ?? 0) >= 0} deltaLoading={showDelta && kpiComparisonLoading && !kpiComparison} drill={true} onClick={() => openDrill({ kind: 'trend' })} />
                    <KPI label="Discount" value={discountBreakdownLoading && !discountBreakdown ? <Bone w={50} h={18} r={4} style={{ margin: '4px auto 0' }} /> : discountBreakdownFailure ? '—' : fmtMoney(discountKpiValue)} delta={showDelta && kpiComparison ? discountDeltaStr : null} up={(discountDelta ?? 0) >= 0} deltaLoading={showDelta && kpiComparisonLoading && !kpiComparison} drill={true} onClick={() => openDrill({ kind: 'discount' })} />
                    <KPI label="AOV" tooltip={AOV_HELPER_TEXT} isMobile value={kpiComparisonLoading && !kpiComparison ? <Bone w={50} h={18} r={4} style={{ margin: '4px auto 0' }} /> : kpiComparisonFailure ? '—' : fmtMoney(aov)} delta={showDelta && kpiComparison ? aovDeltaStr : null} up={(aovDelta ?? 0) >= 0} deltaLoading={showDelta && kpiComparisonLoading && !kpiComparison} drill={false} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <KPI label="Active Offers" value={offersKpiLoading && !offersKpiLoaded ? <Bone w={50} h={18} r={4} style={{ margin: '4px auto 0' }} /> : offersKpiFailure ? '—' : fmtNum(activeOffers)} drill={false} />
                    <KPI
                        label="Top Offer"
                        value={offersKpiLoading && !offersKpiLoaded ? <Bone w={50} h={18} r={4} style={{ margin: '4px auto 0' }} /> : offersKpiFailure ? '—' : topOfferRow ? <span style={{ fontSize: 13 }}>{truncate(localizeCurrencyText(topOfferRow.offerName), 14)}</span> : '—'}
                        delta={topOfferRow ? `${fmtNum(topOfferRow.redemptions)} uses` : undefined}
                        up={true}
                        drill={!!topOfferRow}
                        onClick={topOfferRow ? () => openDrill({ kind: 'offer', id: topOfferRow.offerId, name: topOfferRow.offerName }) : undefined}
                    />
                </div>
                </>
            )}

            {/* CHARTS ROW */}
            <div style={{ display: isDesktop ? 'flex' : 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: isDesktop ? 16 : 10 }}>
                <div className="rpt-card--r16 rpt-card" style={{ flex: 2, padding: isDesktop ? 22 : 14, marginBottom: isDesktop ? 0 : 10 }}>
                    <p className="rpt-section-title" style={{ margin: 0, marginBottom: 2 }}>Redemption Trend</p>
                    <p className="rpt-subtitle" style={{ margin: 0, marginBottom: 12 }}>Daily redemptions across all offers</p>
                    {redemptionTrendLoading && !redemptionTrend ? (
                        <Bone h={trendChartHeight} r={8} />
                    ) : redemptionTrendFailure ? (
                        <SectionError kpiName="redemption trend" onRetry={fetchTrend} size="medium" minHeight={trendChartHeight} />
                    ) : dailyTrend.length === 0 ? (
                        <SectionEmpty kpiName="redemption trend" size="medium" minHeight={trendChartHeight} />
                    ) : (
                    <ResponsiveContainer width="100%" height={trendChartHeight}>
                        <AreaChart data={dailyTrend} margin={{ top: 10, right: dateAxisRightMargin(isDesktop), left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="redemptionGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={T.accent} stopOpacity={0.25} />
                                    <stop offset="100%" stopColor={T.accent} stopOpacity={0.01} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 9, fill: T.t3 }} axisLine={false} tickLine={false} padding={DATE_AXIS_PADDING} tickFormatter={makeDateTickFormatter(dailyTrend.map((d) => d.date))} interval={dateTickInterval(dailyTrend.length)} {...dateAxisTickProps(isDesktop)} />
                            <YAxis tick={{ fontSize: 9, fill: T.t3 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11 }} />
                            <Area type="monotone" dataKey="count" stroke={T.accent} strokeWidth={2.5} fill="url(#redemptionGradient)" />
                        </AreaChart>
                    </ResponsiveContainer>
                    )}
                </div>
                <div className="rpt-card--r16 rpt-card" style={{ flex: 1, padding: isDesktop ? 22 : 14 }}>
                    <p className="rpt-section-title" style={{ margin: 0, marginBottom: 2 }}>Discount by Type</p>
                    <p className="rpt-subtitle" style={{ margin: 0, marginBottom: 12 }}>Cost breakdown by mechanic</p>
                    {discountByTypeLoading && !discountByType ? (
                        <Bone h={230} r={8} />
                    ) : discountByTypeFailure ? (
                        <SectionError kpiName="discount by type" onRetry={fetchDiscountByType} size="medium" minHeight={230} />
                    ) : discountByTypeSegments.length === 0 ? (
                        <SectionEmpty kpiName="discount by type" size="medium" minHeight={230} />
                    ) : (
                    <>
                    <div style={{ position: 'relative', height: 160 }}>
                        <ResponsiveContainer width="100%" height={160}>
                            <PieChart>
                                <Pie data={discountByTypeSegments} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={70} paddingAngle={2} strokeWidth={0}>
                                    {discountByTypeSegments.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Pie>
                                {/* Pin the tooltip to the top of the donut (y=0, x still tracks the
                                    pointer) so it never lands on the centred TOTAL label — the
                                    default follow-cursor position collides with it on touch/mobile. */}
                                <Tooltip position={{ y: 0 }} wrapperStyle={{ zIndex: 10, outline: 'none' }} contentStyle={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11 }} formatter={(v: number) => fmtMoney(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <div style={{ fontSize: 9, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.3 }}>Total</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>{fmtMoney(discountByTypeTotal)}</div>
                        </div>
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {discountByTypeSegments.map(d => (
                            <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                                <span style={{ color: T.t2, flex: 1 }}>{d.name}</span>
                                <span style={{ color: T.t1, fontWeight: 600 }}>{d.pct}</span>
                            </div>
                        ))}
                    </div>
                    </>
                    )}
                </div>
            </div>

            {/* TABLE */}
            <div className="rpt-card--r16 rpt-card" style={{ padding: isDesktop ? 22 : 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <div>
                        <p className="rpt-section-title" style={{ margin: 0 }}>Offers</p>
                        <p className="rpt-subtitle" style={{ margin: 0, marginTop: 2 }}>Ranked by redemptions</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isDesktop ? 12 : 8 }}>
                        <div style={{ fontSize: 11, color: T.t3 }}>{fmtNum(topOffersTotalElements)} offer{topOffersTotalElements === 1 ? '' : 's'}</div>
                        {canExportOffers && (
                            <ReportSectionExportButton
                                iconOnly
                                title={`Offers — ${outletName}`}
                                filename={exportFilename}
                                dateRangeLabel={exportDateRangeLabel}
                                buildExport={buildOffersExport}
                            />
                        )}
                    </div>
                </div>

                {/* CONTROLS ROW (③) — name search (300ms debounce) + status multi-select,
                    with a mobile-only "Sort by" dropdown (desktop sorts via column
                    headers). Server-filtered via `search` + `status`; combines (AND)
                    with the page-level date/location filters. Shown on both layouts. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <span className="material-symbols-rounded" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: T.t3, pointerEvents: 'none' }}>search</span>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search offers"
                            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 30px 8px 32px', fontSize: isDesktop ? 13 : 12, fontFamily: T.font, color: T.t1, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8, outline: 'none' }}
                        />
                        {searchInput && (
                            <button type="button" onClick={() => setSearchInput('')} aria-label="Clear search" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: T.t3, display: 'flex', padding: 2 }}>
                                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                            </button>
                        )}
                    </div>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                            type="button"
                            onClick={() => setStatusMenuOpen((o) => !o)}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: 'auto', minWidth: isDesktop ? 150 : 118, whiteSpace: 'nowrap', padding: '8px 12px', fontSize: isDesktop ? 13 : 12, fontWeight: 500, fontFamily: T.font, color: T.t1, background: T.card, border: `1px solid ${statusFilter.length ? T.accent : T.cardBorder}`, borderRadius: 8, cursor: 'pointer' }}
                        >
                            <span>Status: <span style={{ fontWeight: 600 }}>{statusLabel}</span></span>
                            <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t3 }}>expand_more</span>
                        </button>
                        {statusMenuOpen && (
                            <>
                                <div onClick={() => setStatusMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500 }} />
                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 501, overflow: 'hidden', minWidth: 170 }}>
                                    <button type="button" onClick={() => setStatusFilter([])} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', fontSize: 12, fontWeight: statusFilter.length === 0 ? 700 : 500, color: statusFilter.length === 0 ? T.accent : T.t1, fontFamily: T.font, padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer' }}>
                                        <span className="material-symbols-rounded" style={{ fontSize: 15, width: 15, color: T.accent, visibility: statusFilter.length === 0 ? 'visible' : 'hidden' }}>check</span> All
                                    </button>
                                    {OFFER_STATUS_OPTIONS.map((opt) => {
                                        const on = statusFilter.includes(opt.value);
                                        return (
                                            <button key={opt.value} type="button" onClick={() => toggleStatus(opt.value)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', fontSize: 12, fontWeight: on ? 700 : 500, color: on ? T.accent : T.t1, fontFamily: T.font, padding: '8px 12px', border: 'none', background: on ? `${T.accent}12` : 'transparent', cursor: 'pointer' }}>
                                                <span className="material-symbols-rounded" style={{ fontSize: 15, width: 15, color: T.accent, visibility: on ? 'visible' : 'hidden' }}>check</span> {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {!isDesktop && (
                    <div style={{ position: 'relative' }}>
                        <button
                            type="button"
                            onClick={() => setSortMenuOpen((o) => !o)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%', padding: '8px 12px', fontSize: 12, fontWeight: 500, fontFamily: T.font, color: T.t1, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8, cursor: 'pointer' }}
                        >
                            <span>Sort by: <span style={{ fontWeight: 600 }}>{currentSortLabel}</span></span>
                            <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t3 }}>expand_more</span>
                        </button>
                        {sortMenuOpen && (
                            <>
                                <div onClick={() => setSortMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500 }} />
                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 501, overflow: 'hidden' }}>
                                    {OFFER_SORT_OPTIONS.map((opt) => {
                                        const on = opt.sortBy === sortBy && opt.sortOrder === sortOrder;
                                        return (
                                            <button key={opt.label} type="button" onClick={() => applySort(opt)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', fontSize: 12, fontWeight: on ? 700 : 500, color: on ? T.accent : T.t1, fontFamily: T.font, padding: '9px 12px', border: 'none', background: on ? `${T.accent}12` : 'transparent', cursor: 'pointer' }}>
                                                <span className="material-symbols-rounded" style={{ fontSize: 15, width: 15, color: T.accent, visibility: on ? 'visible' : 'hidden' }}>check</span> {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                )}
                </div>

                {topOffersLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {Array.from({ length: 6 }).map((_, i) => <Bone key={i} h={40} r={8} />)}
                    </div>
                ) : topOffersFailure ? (
                    <SectionError kpiName="top offers" onRetry={fetchTopOffers} size="medium" minHeight={200} />
                ) : pageRows.length === 0 ? (
                    filtersActive ? (
                        <div style={{ minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, textAlign: 'center', padding: 20 }}>
                            <span className="material-symbols-rounded" style={{ fontSize: 28, color: T.t3 }}>search_off</span>
                            <div style={{ fontSize: 13, color: T.t2 }}>No offers match your search or filters</div>
                            <button type="button" onClick={clearOfferFilters} style={{ background: 'none', border: 'none', color: T.accent, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: T.font, padding: 0 }}>Clear</button>
                        </div>
                    ) : (
                        <SectionEmpty kpiName="top offers" size="medium" minHeight={200} />
                    )
                ) : (
                <>
                {isDesktop ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: T.font }}>
                            <thead>
                                <tr>
                                    {[
                                        { key: '#', label: '#', sortable: false },
                                        { key: 'name', label: 'Offer Name', sortable: false },
                                        { key: 'type', label: 'Type', sortable: false },
                                        { key: 'redemptions' as const, label: 'Redemptions', sortable: true },
                                        { key: 'discountGiven' as const, label: 'Discount Given', sortable: true },
                                        { key: 'status', label: 'Status', sortable: false },
                                    ].map(h => (
                                        <th key={h.key} onClick={() => h.sortable && handleSort(h.key as 'redemptions' | 'discountGiven')} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${T.divider}`, cursor: h.sortable ? 'pointer' : 'default', userSelect: 'none' }}>
                                            {h.label} {h.sortable && sortBy === h.key ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((o, i) => (
                                    <tr key={o.offerId} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                        <td style={{ fontSize: 12, color: T.t3, padding: '10px 12px', fontWeight: 600 }}>{(page - 1) * effectiveSize + i + 1}</td>
                                        <td style={{ fontSize: 12, padding: '10px 12px' }}>
                                            <OfferNameDesktopCell name={o.offerName} onOpen={() => openDrill({ kind: 'offer', id: o.offerId, name: o.offerName })} />
                                        </td>
                                        <td style={{ padding: '10px 12px' }}><ApiTypeBadge type={o.offerType} /></td>
                                        <td style={{ fontSize: 12, color: T.t1, padding: '10px 12px', fontWeight: 500 }}>{fmtNum(o.redemptions)}</td>
                                        <td style={{ fontSize: 12, color: T.t1, padding: '10px 12px', fontWeight: 500 }}>{fmtMoney(o.discountGiven)}</td>
                                        <td style={{ padding: '10px 12px' }}><ApiStatusPill status={o.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {pageRows.map(o => (
                            <div key={o.offerId} onClick={() => openDrill({ kind: 'offer', id: o.offerId, name: o.offerName })} style={{ padding: 12, borderRadius: 10, border: `1px solid ${T.cardBorder}`, cursor: 'pointer' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                    <OfferNameMobileCell name={o.offerName} />
                                    <ApiStatusPill status={o.status} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <ApiTypeBadge type={o.offerType} />
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <span style={{ fontSize: 11, color: T.t3 }}>{fmtNum(o.redemptions)} uses</span>
                                        <span style={{ fontSize: 11, color: T.t1, fontWeight: 600 }}>{fmtMoney(o.discountGiven)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ fontSize: isDesktop ? 11 : 10, color: T.t3 }}>{countText}</div>
                        {isCapped && (
                            <div style={{ fontSize: isDesktop ? 10 : 9, color: T.orange, fontWeight: 500 }}>Showing first {ALL_ROWS_CAP} — narrow with search or filters</div>
                        )}
                    </div>
                    {(showSizeSelector || totalPages > 1) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: isDesktop ? 12 : 8, flexWrap: 'wrap' }}>
                            {PageSizeSelector}
                            {totalPages > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={pagBtn(page <= 1, isDesktop)}>← Prev</button>
                                    <span style={{ fontSize: isDesktop ? 11 : 10, color: T.t2, padding: '4px 6px' }}>{page} / {totalPages}</span>
                                    <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={pagBtn(page >= totalPages, isDesktop)}>Next →</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                </>
                )}
            </div>

            <div style={{ height: isDesktop ? 40 : 80 }} />
        </main>
    );
}

export default function OffersReportScreen() {
    const isDesktop = useIsDesktop();
    if (isDesktop) {
        return (
            <DesktopLayout>
                <ErrorBoundary label="Offers Report">
                    <OffersReportContent isDesktop={true} />
                </ErrorBoundary>
            </DesktopLayout>
        );
    }
    return (
        <Phone>
            <TopBar variant="mobile" />
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
                <ErrorBoundary label="Offers Report">
                    <OffersReportContent isDesktop={false} />
                </ErrorBoundary>
            </div>
            <BottomNav />
        </Phone>
    );
}
